import { computeProviderGroups } from "@/lib/self-schedule-provider-groups";
import { addonEntryKey } from "@/lib/service-addon-utils";
import { toMs } from "@/lib/timestamp";

import type { PublicEventTiny } from "@/types/event";
import type { PublicProviderSummary } from "@/types/provider";
import type { AvailabilityAssignment } from "@/types/self-schedule";
import type { PublicServiceDetail } from "@/types/service";

import type { SelectedServiceInstance } from "@/stores/self-schedule-store";

interface ExistingAppointmentSelectionSnapshot {
  selectedServiceInstances: SelectedServiceInstance[];
  selectedAddons: Record<string, string[]>;
  selectedProviderId: string | null;
  serviceInstanceProviderIds: Record<string, string>;
  serviceInstanceProviderOrigins: Record<string, "imported" | "review">;
  providerPreferenceMode: "grouped" | "per-occurrence";
  addonProviderIds: Record<string, string>;
  preferredProviderEntryKeys: Record<string, string>;
  selectedAvailabilityAssignments: AvailabilityAssignment[];
  confirmedAppointmentReview: boolean;
}

/**
 * Rebuild the assignment-only self-schedule state from an existing event.
 * Service segments are ordered by their persisted timeline, and add-ons are
 * matched to the parent service declared by their ServiceAddon relationship.
 */
export function buildExistingAppointmentSelectionSnapshot(
  appointment: PublicEventTiny,
  services: PublicServiceDetail[],
  providers: PublicProviderSummary[],
  locationId: string | null,
  generateInstanceId: () => string,
): ExistingAppointmentSelectionSnapshot {
  // Already service-only: the public payload filters the other segment kinds out.
  const serviceSegments = [...appointment.segments].sort(
    (left, right) =>
      toMs(left.start) - toMs(right.start) ||
      toMs(left.end) - toMs(right.end) ||
      left.id.localeCompare(right.id),
  );
  const availableProviderIds = new Set(providers.map(({ id }) => id));
  const selectedServiceInstances: SelectedServiceInstance[] = [];
  const selectedAddons: Record<string, string[]> = {};
  const addonProviderIds: Record<string, string> = {};
  const providerByInstanceId = new Map<string, string | null>();
  const occurrenceCounts = new Map<string, number>();
  const parentOccurrences = new Map<
    string,
    { occurrenceIndex: number; start: string; instanceId: string }[]
  >();
  const parentServiceIdByAddonId = new Map(
    services.flatMap((service) =>
      (service.addons ?? []).map((addon) => [addon.id, service.id] as const),
    ),
  );

  for (const segment of serviceSegments) {
    if (segment.serviceAddonId) continue;

    const occurrenceIndex = (occurrenceCounts.get(segment.service.id) ?? 0) + 1;
    occurrenceCounts.set(segment.service.id, occurrenceIndex);
    const instanceId = generateInstanceId();
    selectedServiceInstances.push({
      id: segment.service.id,
      instanceId,
      name: segment.service.name,
    });
    providerByInstanceId.set(instanceId, segment.providerId ?? null);
    parentOccurrences.set(segment.service.id, [
      ...(parentOccurrences.get(segment.service.id) ?? []),
      { occurrenceIndex, start: segment.start, instanceId },
    ]);
  }

  for (const segment of serviceSegments) {
    if (!segment.serviceAddonId) continue;

    const parentServiceId = parentServiceIdByAddonId.get(
      segment.serviceAddonId,
    );
    const occurrences = parentServiceId
      ? parentOccurrences.get(parentServiceId)
      : undefined;
    if (!parentServiceId || !occurrences?.length) continue;

    const parentOccurrence =
      [...occurrences]
        .reverse()
        .find(({ start }) => toMs(start) <= toMs(segment.start)) ??
      occurrences[0];
    const key = `${parentServiceId}:${parentOccurrence.occurrenceIndex}`;
    selectedAddons[key] = [
      ...(selectedAddons[key] ?? []),
      segment.serviceAddonId,
    ];
    if (segment.providerId) {
      const ordinal = selectedAddons[key].filter(
        (id) => id === segment.serviceAddonId,
      ).length;
      addonProviderIds[
        addonEntryKey(
          parentOccurrence.instanceId,
          segment.serviceAddonId,
          ordinal,
        )
      ] = segment.providerId;
    }
  }

  const groups = computeProviderGroups(
    selectedServiceInstances,
    services,
    providers,
    locationId,
  );
  const serviceInstanceProviderIds: Record<string, string> = {};

  // Validate parents independently before deciding whether ordinary grouped
  // preferences can reproduce the saved occurrence assignments.
  const occurrenceGroups = computeProviderGroups(
    selectedServiceInstances,
    services,
    providers,
    locationId,
    "per-occurrence",
  );
  for (const group of occurrenceGroups) {
    for (const instance of group.instances) {
      const providerId = providerByInstanceId.get(instance.instanceId);
      if (providerId && group.eligibleProviderIds.includes(providerId)) {
        serviceInstanceProviderIds[instance.instanceId] = providerId;
      }
    }
  }

  const needsPerOccurrencePreferences =
    Object.keys(addonProviderIds).length > 0 ||
    groups.some((group) => {
      const ids = group.instances.map(
        (instance) => serviceInstanceProviderIds[instance.instanceId],
      );
      return ids.some((id) => id !== ids[0]);
    });
  const providerPreferenceMode = needsPerOccurrencePreferences
    ? "per-occurrence"
    : "grouped";
  const effectiveGroups = needsPerOccurrencePreferences
    ? occurrenceGroups
    : groups;

  const baseProviderIds = selectedServiceInstances.map(
    ({ instanceId }) => serviceInstanceProviderIds[instanceId],
  );
  const singleProviderId =
    !needsPerOccurrencePreferences &&
    groups.length === 1 &&
    baseProviderIds.length > 0 &&
    baseProviderIds.every((providerId) => providerId === baseProviderIds[0])
      ? (baseProviderIds[0] ?? null)
      : null;
  const confirmedAppointmentReview =
    effectiveGroups.length > 0 &&
    effectiveGroups.every(
      (group) =>
        group.eligibleProviderIds.length > 0 &&
        group.instances.every(
          ({ instanceId }) => serviceInstanceProviderIds[instanceId],
        ),
    );

  const selectedAvailabilityAssignments = serviceSegments.flatMap((segment) =>
    segment.providerId && availableProviderIds.has(segment.providerId)
      ? [{ serviceId: segment.service.id, providerId: segment.providerId }]
      : [],
  );

  return {
    selectedServiceInstances,
    selectedAddons,
    selectedProviderId: singleProviderId,
    serviceInstanceProviderIds,
    serviceInstanceProviderOrigins: Object.fromEntries(
      Object.keys(serviceInstanceProviderIds).map((id) => [
        id,
        "imported" as const,
      ]),
    ),
    providerPreferenceMode,
    addonProviderIds,
    preferredProviderEntryKeys: {},
    selectedAvailabilityAssignments,
    confirmedAppointmentReview,
  };
}
